import { Injectable, signal } from '@angular/core';
import type { WritableSignal } from '@angular/core';

export type IdType = string | number;

export type NavigationTarget = {
    entityId: string;
    blockId?: string;
    sectionId?: string;
    columnId?: string;
    gridId?: string;
};

@Injectable({
    providedIn: 'root',
})
export class NavigationDrawerService {
    private readonly _target = signal<NavigationTarget | null>(null);

    public readonly target = this._target.asReadonly();

    private readonly blockIds = signal<IdType[]>([]);
    private readonly rowIds = signal<IdType[]>([]);
    private readonly columnIds = signal<IdType[]>([]);
    private readonly gridIds = signal<IdType[]>([]);
    private readonly tabIds = signal<IdType[]>([]);

    public selectInNavigation(target: NavigationTarget): void {
        this._target.set({ ...target });
    }

    public clearTarget(): void {
        this._target.set(null);
    }

    public addBlockId(id: IdType): void {
        this.addId(this.blockIds, id);
    }

    public removeBlockId(id: IdType): void {
        this.removeId(this.blockIds, id);
    }

    public hasBlockId(id: IdType): boolean {
        return this.blockIds().includes(id);
    }

    public addRowId(id: IdType): void {
        this.addId(this.rowIds, id);
    }

    public removeRowId(id: IdType): void {
        this.removeId(this.rowIds, id);
    }

    public hasRowId(id: IdType): boolean {
        return this.rowIds().includes(id);
    }

    public addColumnId(id: IdType): void {
        this.addId(this.columnIds, id);
    }

    public removeColumnId(id: IdType): void {
        this.removeId(this.columnIds, id);
    }

    public hasColumnId(id: IdType): boolean {
        return this.columnIds().includes(id);
    }

    public addGridId(id: IdType): void {
        this.addId(this.gridIds, id);
    }

    public removeGridId(id: IdType): void {
        this.removeId(this.gridIds, id);
    }

    public hasGridId(id: IdType): boolean {
        return this.gridIds().includes(id);
    }

    public addTabId(id: IdType): void {
        this.addId(this.tabIds, id);
    }

    public removeTabId(id: IdType): void {
        this.removeId(this.tabIds, id);
    }

    public hasTabId(id: IdType): boolean {
        return this.tabIds().includes(id);
    }

    private addId(set: WritableSignal<IdType[]>, id: IdType): void {
        if (!set().includes(id)) {
            set.update((current) => [...current, id]);
        }
    }

    private removeId(set: WritableSignal<IdType[]>, id: IdType): void {
        const current = set();

        if (!current.includes(id)) return;

        set.set(current.filter((x) => x !== id));
    }
}
